import { useWindowEvent } from '@mantine/hooks'
import { useState } from 'react'
import type { JsonValue } from 'type-fest'
import { openSignModal } from 'src/components/Account/utils'
import {
  isPaymentAvailable,
  openMembershipPaymentUnavailableNotice,
} from 'src/components/Membership/MembershipPaymentUnavailableNotice'
import { useResetAllCouponStates } from 'src/features/Coupon/Regular/states'
import NewDecorationCongratDialog from 'src/features/GrowthEvent/common/Decoration/NewDecorationCongratDialog'
import {
  getUANewbiePackTierByItemId,
  useUANewbiePackSnapshot,
} from 'src/features/UAPromotion/useUANewbiePack'
import { useMyInfo } from 'src/services/apollo/auth'
import { refetchGetAllPaymentItems } from 'src/services/apollo/payment'
import { useListMyQuotaLogs, useMyQuota } from 'src/services/apollo/quota'
import { useInvalidateExtraPackageList } from 'src/services/orpc/extraPackage'
import { useMatches } from 'src/utils/router/route'
import { track } from 'src/utils/tracker'
import type { ExtraPackage, ExtraPackagePromotionOption } from '../api/types'
import { usePurchaseExtraPackage } from '../api/usePurchaseExtraPackage'
import { partitionPackageContents } from '../domain/packageContents'

interface UsePackPurchaseFlowArgs {
  promotionOption: ExtraPackagePromotionOption | null
  trackingArgs?: Record<string, JsonValue>
  afterSuccess?: () => void
}

/**
 * Encapsulate the "click a card → purchase → show success animation →
 * refresh caches" flow. Returns the `submit` callback for cards plus the
 * id of the pack currently flashing success state.
 *
 * Behavior matches the legacy ExtraPackItems flow exactly.
 */
export function usePackPurchaseFlow(args: UsePackPurchaseFlowArgs) {
  const { data: userInfo } = useMyInfo()
  const { data: newbiePackSnapshot } = useUANewbiePackSnapshot()
  const purchase = usePurchaseExtraPackage({
    promotionOption: args.promotionOption,
  })
  const [showingSuccess, setShowingSuccess] = useState<string | null>(null)
  const { refetch: refreshCreditHistory } = useListMyQuotaLogs({ last: 20 })
  const { refetch: refreshCreditBalance } = useMyQuota()
  const invalidateExtraPackageList = useInvalidateExtraPackageList()
  const resetAllCouponStates = useResetAllCouponStates()
  // Surface the active route's `handle.name` for analytics — matches the
  // legacy `useMembershipActions.purchaseExtraPackage` behavior.
  const matches = useMatches()
  const scene = (matches.at(-1)?.handle as { name?: string })?.name

  useWindowEvent('focus', resetAllCouponStates)

  async function submit(extraPackage: ExtraPackage) {
    if (!userInfo) return openSignModal(undefined, { fromPath: 'membership' })
    if (!isPaymentAvailable())
      return openMembershipPaymentUnavailableNotice({ type: 'unavailable' })
    if (showingSuccess) return

    track('refuel_purchase_click', {
      extraPackageId: extraPackage.id,
      ...args.trackingArgs,
    })

    const newbiePackTier = getUANewbiePackTierByItemId(
      newbiePackSnapshot,
      extraPackage.id,
      extraPackage.legacyItemId,
    )
    if (newbiePackTier) {
      track('newbie_pack_clicked', {
        entry_point: 'credits_page',
        target_tier: newbiePackTier.tierCode,
      })
    }

    const result = await purchase({
      extraPackageId: extraPackage.id,
      prices: extraPackage.prices,
      scene,
    })
    if (result) {
      setShowingSuccess(extraPackage.id)
      setTimeout(() => {
        setShowingSuccess(null)
        args.afterSuccess?.()
        refreshCreditBalance()
        refreshCreditHistory()
        // Refresh both transports: oRPC `extraPackage.list` (the new
        // primary source) and Apollo `paymentItems` (still used by
        // membership / growth event surfaces during the rollout).
        invalidateExtraPackageList()
        refetchGetAllPaymentItems()
        resetAllCouponStates()

        const { decorations } = partitionPackageContents(
          extraPackage.packageContents ?? [],
        )
        const decorationCodes = decorations.map(d => d.code)
        if (decorationCodes.length > 0) {
          NewDecorationCongratDialog.open(decorationCodes, {
            trackingArgs: {
              source: 'credit_pack_purchase',
              extraPackageId: extraPackage.id,
              ...args.trackingArgs,
            },
          })
        }
      }, 2000)
    }
  }

  return { submit, showingSuccess }
}
